import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ProtectedRoute from './components/common/ProtectedRoute';
import CustomerNav from './components/common/CustomerNav';
import EmployeeNav from './components/employee/EmployeeNav';

// Public pages
import LandingPage from './pages/public/LandingPage';
import Catalog from './pages/public/Catalog';

// Customer pages
import Login from './pages/customer/Login';
import Signup from './pages/customer/Signup';
import ForgotPassword from './pages/customer/ForgotPassword';
import ResetPassword from './pages/customer/ResetPassword';
import Dashboard from './pages/customer/Dashboard';
import Products from './pages/customer/Products';
import ProductDetail from './pages/customer/ProductDetail';
import NewOrder from './pages/customer/NewOrder';
import Orders from './pages/customer/Orders';
import OrderDetail from './pages/customer/OrderDetail';
import Invoices from './pages/customer/Invoices';
import Profile from './pages/customer/Profile';

// Employee pages
import EmployeeDashboard from './pages/employee/Dashboard';
import EmployeeOrders from './pages/employee/Orders';
import EmployeeOrderDetail from './pages/employee/OrderDetail';
import EmployeeCustomers from './pages/employee/Customers';
import EmployeeProducts from './pages/employee/Products';
import EmployeeGrowers from './pages/employee/Growers';
import EmployeeGrainVarieties from './pages/employee/GrainVarieties';
import EmployeeNewOrder from './pages/employee/NewOrder';
import PrintLabel from './pages/employee/PrintLabel';
import PrintPacklist from './pages/employee/PrintPacklist';
import PrintQRSheet from './pages/employee/PrintQRSheet';
import OnlineOrders from './pages/employee/OnlineOrders';
import AccountApprovals from './pages/employee/AccountApprovals';
import EmployeeAccount from './pages/employee/Account';
import MillEmails from './pages/employee/MillEmails';
import Budget from './pages/employee/Budget';
import EmployeeInvoices from './pages/employee/Invoices';

import { useAuth } from './context/AuthContext';
import './App.css';

/**
 * Role-aware redirect — sends authenticated users to their appropriate
 * dashboard. Unauthenticated users fall through to /login.
 */
const RoleRedirect = ({ customerPath = '/dashboard', employeePath = '/employee/dashboard' }) => {
  const { isAuthenticated, isEmployee } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={isEmployee ? employeePath : customerPath} replace />;
};

/**
 * Landing page for unauthenticated visitors.
 * Authenticated users get redirected to their dashboard.
 */
const LandingOrRedirect = () => {
  const { isAuthenticated, isEmployee } = useAuth();
  if (isAuthenticated) {
    return <Navigate to={isEmployee ? '/employee/dashboard' : '/dashboard'} replace />;
  }
  return <LandingPage />;
};

const ProtectedLayout = () => (
  <ProtectedRoute requireCustomer>
    <>
      <CustomerNav />
      <Outlet />
    </>
  </ProtectedRoute>
);

const EmployeeLayout = () => (
  <ProtectedRoute requireEmployee>
    <div className="employee-layout">
      <EmployeeNav />
      <main className="employee-layout__content">
        <Outlet />
      </main>
    </div>
  </ProtectedRoute>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/catalog" element={<Catalog />} />

        {/* Landing page — public root (authenticated users redirect to dashboard) */}
        <Route path="/" element={<LandingOrRedirect />} />

        {/* Protected customer routes */}
        <Route element={<ProtectedLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/order/new" element={<NewOrder />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/profile" element={<Profile />} />
        </Route>

        {/* Protected employee routes — nested under /employee with auth gate */}
        <Route path="/employee" element={<EmployeeLayout />}>
          <Route index element={<Navigate to="/employee/dashboard" replace />} />
          <Route path="dashboard" element={<EmployeeDashboard />} />
          <Route path="orders" element={<EmployeeOrders />} />
          <Route path="orders/:id" element={<EmployeeOrderDetail />} />
          <Route path="customers" element={<EmployeeCustomers />} />
          <Route path="customers/new-order" element={<EmployeeNewOrder />} />
          <Route path="products" element={<EmployeeProducts />} />
          <Route path="growers" element={<EmployeeGrowers />} />
          <Route path="grain-varieties" element={<EmployeeGrainVarieties />} />
          <Route path="online-orders" element={<OnlineOrders />} />
          <Route path="account-approvals" element={<AccountApprovals />} />
          <Route path="invoices" element={<EmployeeInvoices />} />
          <Route path="mill-emails" element={<MillEmails />} />
          <Route path="budget" element={<Budget />} />
          <Route path="account" element={<EmployeeAccount />} />
        </Route>

        {/* Print label — standalone nav-free page for label printing */}
        <Route
          path="/print-label"
          element={
            <ProtectedRoute requireEmployee>
              <PrintLabel />
            </ProtectedRoute>
          }
        />

        {/* Print packlist — standalone nav-free page for order packlists */}
        <Route
          path="/print-packlist"
          element={
            <ProtectedRoute requireEmployee>
              <PrintPacklist />
            </ProtectedRoute>
          }
        />

        {/* Print QR sheet — standalone nav-free page for QR code grids */}
        <Route
          path="/print-qr-sheet"
          element={
            <ProtectedRoute requireEmployee>
              <PrintQRSheet />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to role-appropriate dashboard */}
        <Route path="*" element={<RoleRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
